const APP_UPDATE_QUERY = '_appUpdate';

/**
 * Remove cache-bust query param after a hard reload (keeps URL clean).
 */
export function stripAppUpdateQueryFromUrl() {
    if (typeof window === 'undefined') return;
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(APP_UPDATE_QUERY)) return;
        url.searchParams.delete(APP_UPDATE_QUERY);
        const next = `${url.pathname}${url.search}${url.hash}`;
        window.history.replaceState(window.history.state, '', next);
    } catch {
        // ignore
    }
}

async function clearCacheStorage() {
    if (typeof caches === 'undefined' || !caches?.keys) return;
    try {
        const keys = await caches.keys();
        await Promise.allSettled(keys.map((key) => caches.delete(key)));
    } catch {
        // ignore
    }
}

async function unregisterServiceWorkers() {
    if (!navigator?.serviceWorker?.getRegistrations) return;
    try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.allSettled(registrations.map((reg) => reg.unregister()));
    } catch {
        // ignore
    }
}

/**
 * Best-effort document preload with cache bypass before navigation.
 */
async function bustDocumentCache() {
    try {
        const base = `${window.location.origin}${window.location.pathname || '/'}`;
        await fetch(base, {
            method: 'GET',
            cache: 'reload',
            credentials: 'same-origin',
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
            },
        });
    } catch {
        // ignore — navigation still proceeds
    }
}

/**
 * Hard reload — clears Cache Storage + service workers, then navigates with a cache-bust
 * query (similar to Ctrl+Shift+R for a Vite SPA with fixed asset names).
 *
 * @param {{ acknowledgedBuildId?: string, buildStorageKey?: string }} [options]
 */
export async function hardReloadApp(options = {}) {
    const { acknowledgedBuildId, buildStorageKey } = options;

    if (acknowledgedBuildId && buildStorageKey) {
        try {
            localStorage.setItem(buildStorageKey, acknowledgedBuildId);
        } catch {
            // ignore
        }
    }

    await Promise.all([clearCacheStorage(), unregisterServiceWorkers()]);
    await bustDocumentCache();

    const url = new URL(window.location.href);
    url.searchParams.delete(APP_UPDATE_QUERY);
    url.searchParams.set(APP_UPDATE_QUERY, String(Date.now()));

    // replace() avoids back-button returning to stale bundle
    window.location.replace(url.toString());
}
